import { toast } from "sonner";
import { i18next } from "@envirohub/i18n";
import { ApiError, getApiErrorMessage, normalizeError } from "./errors";

/** Error categories that should never raise a toast. */
const SILENT_CODES: ReadonlyArray<ApiError["code"]> = [
  "canceled",
  // 401 triggers a redirect to /login; a toast would flash and disappear.
  "unauthorized",
];

/**
 * Translate an {@link ApiError} using the i18next singleton. Safe to call
 * outside React (e.g. from the axios interceptor or React Query caches).
 */
export function translateApiError(error: ApiError): string {
  return getApiErrorMessage(error, (key, opts) => i18next.t(key, opts));
}

export interface ReportApiErrorOptions {
  /** Suppress the toast (caller will surface the error inline instead). */
  silent?: boolean;
  /** Override the toast message. */
  message?: string;
}

/**
 * Centralized, user-facing error reporting. Normalizes the error, then shows
 * a deduplicated toast (keyed by category so concurrent identical failures
 * collapse into one). Returns the normalized {@link ApiError} for chaining.
 */
export function reportApiError(error: unknown, options: ReportApiErrorOptions = {}): ApiError {
  const apiError = normalizeError(error);

  if (options.silent || SILENT_CODES.includes(apiError.code)) {
    return apiError;
  }

  const message = options.message ?? translateApiError(apiError);
  toast.error(message, { id: `api-error:${apiError.code}` });
  return apiError;
}
